import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ImpersonationUrl, ImpersonationUser } from '@pulpo/pulpo-models';
import { APP_CONFIG } from '@pulpo/app-config';
import ApiService from './api.service.abstract';
import { SILENT_ERROR } from './silent-error.context';

/**
 * Opening a session on the sales site as an agency user (PUL-2840).
 *
 * Reserved to members of the producer group holding the `admin` permission; anyone else gets
 * a 403. That right cannot be checked client-side — `admin` is an aggregator, filtered out of
 * the permissions `/user-groups/{userId}/groups` returns — so `searchUsers` doubles as the
 * capability probe: hide the feature when it fails.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService extends ApiService<ImpersonationUser> {
  constructor(@Inject(APP_CONFIG) environment: any, private http: HttpClient) {
    super(environment, http, 'user/api/impersonation');
    if (environment.name === 'localDev') {
      this.url = 'http://localhost:8095';
    }
  }

  /**
   * Impersonable agency members, searched on name / email / login.
   *
   * `silent` keeps a failure away from the global error snackbar: the capability probe expects a
   * 403 for anyone without the right.
   */
  searchUsers(
    search?: string,
    silent = false
  ): Observable<HttpResponse<ImpersonationUser[]>> {
    return this.http.get<ImpersonationUser[]>(
      `${this.url}/${this.endpoint}/users`,
      {
        params: search ? { search } : {},
        observe: 'response',
        context: new HttpContext().set(SILENT_ERROR, silent),
      }
    );
  }

  /**
   * Ask for a hand-off URL opening a session as `userId`.
   *
   * The token in the returned URL lives a few minutes and is spent on arrival: nothing to
   * store, nothing to replay.
   */
  loginAs(userId: string): Observable<HttpResponse<ImpersonationUrl>> {
    return this.http.post<ImpersonationUrl>(
      `${this.url}/${this.endpoint}/login-as`,
      { userId },
      { observe: 'response' }
    );
  }
}
